import { useState, useEffect, useRef } from "react"
import { AbstractVideoController } from "../AbstractVideoController"
import {
    GetOrCreateVideoControllerParameters,
    VideoControllerFactory,
} from "../VideoControllerFactory"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type GuardClass<T> = new (...args: any) => T

function useVideoControllerImplementation<T>({
    flightId,
    options,
    guardClass,
}: GetOrCreateVideoControllerParameters & {
    guardClass?: GuardClass<T>
}) {
    const [controller, setController] = useState<T | AbstractVideoController | undefined>()
    const optionsRef = useRef(options)
    useEffect(() => {
        optionsRef.current = options
    }, [options])
    useEffect(() => {
        const trySetController = async () => {
            if (!flightId) {
                setController(undefined)
                return
            }
            let controller: T | AbstractVideoController | undefined
            if (guardClass) {
                controller = await VideoControllerFactory.getOrCreateGuardedControllerForFlight({
                    flightId,
                    options: optionsRef.current,
                    guardClass,
                })
            } else {
                controller = await VideoControllerFactory.getOrCreateControllerForFlight({
                    flightId,
                    options: optionsRef.current,
                })
            }
            setController(controller)
        }
        const flushListener = () => {
            setController(undefined)
        }
        VideoControllerFactory.emitter.on("controllerAvailabilityChanged", trySetController)
        VideoControllerFactory.emitter.on("flushAllControllers", flushListener)
        void trySetController()
        return () => {
            VideoControllerFactory.emitter.off("controllerAvailabilityChanged", trySetController)
            VideoControllerFactory.emitter.off("flushAllControllers", flushListener)
        }
    }, [flightId, guardClass])
    return controller
}
export function useVideoControllerForFlight({
    flightId,
    options,
}: GetOrCreateVideoControllerParameters) {
    return useVideoControllerImplementation({
        flightId,
        options,
    }) as AbstractVideoController | undefined
}
export function useGuardedVideoControllerForFlight<T>({
    flightId,
    options,
    guardClass,
}: GetOrCreateVideoControllerParameters & {
    guardClass: GuardClass<T>
}) {
    return useVideoControllerImplementation({
        flightId,
        options,
        guardClass,
    }) as T | undefined
}
